import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import { MONTHS, monthLabel, fmtMoney, fmtPct } from '../utils/format';
import KpiStrip from './KpiStrip';
import InsightsPanel from './InsightsPanel';
import PnLComparisonTable from './PnLComparisonTable';
import WaterfallChart from './WaterfallChart';
import TrendChart from './TrendChart';
import YearComparisonView from './YearComparisonView';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 5, CURRENT_YEAR - 4, CURRENT_YEAR - 3, CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

const MODES = [
    { id: 'compare',     label: 'Compare two periods' },
    { id: 'trend',       label: 'Month across years' },
    { id: 'year',        label: 'Full year view' },
    { id: 'yearCompare', label: 'Year vs Year' }
];

const AnalyticsTab = () => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [mode, setMode] = useState('compare');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Compare mode
    const today = new Date();
    const [filters, setFilters] = useState({
        businessUnit: '',
        // Period A = this month
        periodAMonth: today.getMonth() + 1,
        periodAYear: today.getFullYear(),
        // Period B = same month last year
        periodBMonth: today.getMonth() + 1,
        periodBYear: today.getFullYear() - 1,
        // Trend mode
        trendMonth: today.getMonth() + 1,
        // Year mode
        yearViewYear: today.getFullYear(),
        // Year-vs-Year mode (A = current, B = baseline — same convention as `compare`)
        yearCompareYearA: today.getFullYear(),
        yearCompareYearB: today.getFullYear() - 1
    });

    const [comparison, setComparison] = useState(null);
    const [trend, setTrend] = useState([]);
    const [yearView, setYearView] = useState(null);
    const [yearCompare, setYearCompare] = useState(null);

    const setF = (patch) => setFilters(p => ({ ...p, ...patch }));

    // Load BUs and pick the first as default
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axiosInstance.get('/api/financial-reports/business-units');
                if (data.success) {
                    setBusinessUnits(data.data || []);
                    if (data.data?.length && !filters.businessUnit) {
                        setF({ businessUnit: data.data[0] });
                    }
                }
            } catch (err) {
                console.error('BU load failed', err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadComparison = useCallback(async () => {
        if (!filters.businessUnit) return;
        setLoading(true); setError('');
        try {
            const { data } = await axiosInstance.get('/api/financial-reports/comparison', {
                params: {
                    businessUnit: filters.businessUnit,
                    periodAYear: filters.periodAYear,
                    periodAMonth: filters.periodAMonth,
                    periodBYear: filters.periodBYear,
                    periodBMonth: filters.periodBMonth
                }
            });
            if (data.success) setComparison(data.data);
        } catch (err) {
            setComparison(null);
            const msg = err.response?.data?.message || 'Failed to load comparison';
            const missing = err.response?.data?.missing;
            if (missing) {
                const m = [];
                if (missing.periodA) m.push(`${monthLabel(missing.periodA.month)} ${missing.periodA.year}`);
                if (missing.periodB) m.push(`${monthLabel(missing.periodB.month)} ${missing.periodB.year}`);
                setError(`${msg} Missing: ${m.join(', ')}.`);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [filters.businessUnit, filters.periodAMonth, filters.periodAYear, filters.periodBMonth, filters.periodBYear]);

    const loadTrend = useCallback(async () => {
        if (!filters.businessUnit) return;
        setLoading(true); setError('');
        try {
            const { data } = await axiosInstance.get('/api/financial-reports/trend', {
                params: { businessUnit: filters.businessUnit, month: filters.trendMonth }
            });
            if (data.success) setTrend(data.data || []);
        } catch (err) {
            setTrend([]);
            setError(err.response?.data?.message || 'Failed to load trend');
        } finally {
            setLoading(false);
        }
    }, [filters.businessUnit, filters.trendMonth]);

    const loadYearView = useCallback(async () => {
        if (!filters.businessUnit) return;
        setLoading(true); setError('');
        try {
            const { data } = await axiosInstance.get('/api/financial-reports/year-view', {
                params: { businessUnit: filters.businessUnit, year: filters.yearViewYear }
            });
            if (data.success) setYearView(data.data);
        } catch (err) {
            setYearView(null);
            setError(err.response?.data?.message || 'Failed to load year view');
        } finally {
            setLoading(false);
        }
    }, [filters.businessUnit, filters.yearViewYear]);

    const loadYearCompare = useCallback(async () => {
        if (!filters.businessUnit) return;
        if (filters.yearCompareYearA === filters.yearCompareYearB) {
            setYearCompare(null);
            setError('Pick two different years to compare.');
            return;
        }
        setLoading(true); setError('');
        try {
            const { data } = await axiosInstance.get('/api/financial-reports/year-comparison', {
                params: {
                    businessUnit: filters.businessUnit,
                    yearA: filters.yearCompareYearA,
                    yearB: filters.yearCompareYearB
                }
            });
            if (data.success) setYearCompare(data.data);
        } catch (err) {
            setYearCompare(null);
            setError(err.response?.data?.message || 'Failed to load year comparison');
        } finally {
            setLoading(false);
        }
    }, [filters.businessUnit, filters.yearCompareYearA, filters.yearCompareYearB]);

    useEffect(() => {
        if (mode === 'compare')     loadComparison();
        if (mode === 'trend')       loadTrend();
        if (mode === 'year')        loadYearView();
        if (mode === 'yearCompare') loadYearCompare();
    }, [mode, loadComparison, loadTrend, loadYearView, loadYearCompare]);

    const yearMonthsChart = useMemo(() => {
        if (!yearView) return [];
        return yearView.months.map(m => ({
            label: monthLabel(m.month, true),
            sales: m.sales || 0,
            directExpenses: m.directExpenses || 0,
            indirectExpenses: m.indirectExpenses || 0,
            netProfit: m.netProfit || 0,
            netMargin: m.netMargin || 0
        }));
    }, [yearView]);

    return (
        <div className="fr-analytics-tab">
            {/* Mode toggle + global filters */}
            <section className="fr-card fr-controls-card">
                <div className="fr-mode-toggle">
                    {MODES.map(m => (
                        <button
                            key={m.id}
                            className={`fr-mode-btn ${mode === m.id ? 'active' : ''}`}
                            onClick={() => setMode(m.id)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                <div className="fr-controls-grid">
                    <div className="fr-form-group">
                        <label>Business Unit</label>
                        <select
                            value={filters.businessUnit}
                            onChange={e => setF({ businessUnit: e.target.value })}
                        >
                            <option value="">— Select —</option>
                            {businessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                        </select>
                    </div>

                    {mode === 'compare' && (
                        <>
                            <div className="fr-form-group">
                                <label>Period A — Month</label>
                                <select value={filters.periodAMonth} onChange={e => setF({ periodAMonth: Number(e.target.value) })}>
                                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                                </select>
                            </div>
                            <div className="fr-form-group">
                                <label>Period A — Year</label>
                                <select value={filters.periodAYear} onChange={e => setF({ periodAYear: Number(e.target.value) })}>
                                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div className="fr-form-group">
                                <label>Period B — Month</label>
                                <select value={filters.periodBMonth} onChange={e => setF({ periodBMonth: Number(e.target.value) })}>
                                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                                </select>
                            </div>
                            <div className="fr-form-group">
                                <label>Period B — Year</label>
                                <select value={filters.periodBYear} onChange={e => setF({ periodBYear: Number(e.target.value) })}>
                                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </>
                    )}

                    {mode === 'trend' && (
                        <div className="fr-form-group">
                            <label>Month to track</label>
                            <select value={filters.trendMonth} onChange={e => setF({ trendMonth: Number(e.target.value) })}>
                                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </select>
                        </div>
                    )}

                    {mode === 'year' && (
                        <div className="fr-form-group">
                            <label>Year</label>
                            <select value={filters.yearViewYear} onChange={e => setF({ yearViewYear: Number(e.target.value) })}>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    )}

                    {mode === 'yearCompare' && (
                        <>
                            <div className="fr-form-group">
                                <label>Current Year</label>
                                <select value={filters.yearCompareYearA} onChange={e => setF({ yearCompareYearA: Number(e.target.value) })}>
                                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div className="fr-form-group">
                                <label>Compare With</label>
                                <select value={filters.yearCompareYearB} onChange={e => setF({ yearCompareYearB: Number(e.target.value) })}>
                                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {error && <div className="fr-error-banner">{error}</div>}
            {loading && <div className="fr-loading-banner">Loading…</div>}

            {/* COMPARE MODE */}
            {mode === 'compare' && comparison && !loading && (
                <>
                    <KpiStrip comparison={comparison} />
                    <div className="fr-analytics-grid">
                        <div className="fr-analytics-col-wide">
                            <InsightsPanel
                                insights={comparison.insights}
                                periodA={comparison.periodA}
                                periodB={comparison.periodB}
                            />
                        </div>
                        <div className="fr-analytics-col-wide">
                            <WaterfallChart comparison={comparison} />
                        </div>
                    </div>
                    <section className="fr-card">
                        <PnLComparisonTable comparison={comparison} />
                    </section>
                </>
            )}

            {/* TREND MODE */}
            {mode === 'trend' && !loading && (
                <>
                    {trend.length === 0 ? (
                        <div className="fr-empty-banner">
                            No data uploaded yet for {monthLabel(filters.trendMonth)} across years.
                        </div>
                    ) : (
                        <>
                            <TrendChart
                                data={trend}
                                title={`${monthLabel(filters.trendMonth)} performance across years`}
                                subtitle={`${filters.businessUnit} — every year you've uploaded data for ${monthLabel(filters.trendMonth)}.`}
                            />
                            <section className="fr-card">
                                <div className="fr-card-header">
                                    <h3>Year-over-year table</h3>
                                </div>
                                <div className="fr-table-wrap">
                                    <table className="fr-table">
                                        <thead>
                                            <tr>
                                                <th>Year</th>
                                                <th className="fr-num">Sales</th>
                                                <th className="fr-num">Gross Profit</th>
                                                <th className="fr-num">Gross %</th>
                                                <th className="fr-num">Indirect Exp</th>
                                                <th className="fr-num">Net Profit</th>
                                                <th className="fr-num">Net %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trend.map(r => (
                                                <tr key={r.year}>
                                                    <td><strong>{r.year}</strong></td>
                                                    <td className="fr-num">{fmtMoney(r.sales)}</td>
                                                    <td className="fr-num">{fmtMoney(r.grossProfit)}</td>
                                                    <td className="fr-num">{fmtPct(r.grossMargin)}</td>
                                                    <td className="fr-num">{fmtMoney(r.indirectExpenses)}</td>
                                                    <td className="fr-num">{fmtMoney(r.netProfit)}</td>
                                                    <td className="fr-num">{fmtPct(r.netMargin)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </>
                    )}
                </>
            )}

            {/* YEAR MODE */}
            {mode === 'year' && yearView && !loading && (
                <>
                    <section className="fr-card fr-ytd-card">
                        <div className="fr-card-header">
                            <h3>Year-to-Date · {yearView.year}</h3>
                            <p>{yearView.businessUnit} — totals across all uploaded months in this year.</p>
                        </div>
                        <div className="fr-ytd-strip">
                            <div className="fr-ytd-item"><span>Sales</span><strong>{fmtMoney(yearView.ytdTotals.sales)}</strong></div>
                            <div className="fr-ytd-item"><span>Direct Expenses</span><strong>{fmtMoney(yearView.ytdTotals.directExpenses)}</strong></div>
                            <div className="fr-ytd-item"><span>Gross Profit</span><strong>{fmtMoney(yearView.ytdTotals.grossProfit)}</strong></div>
                            <div className="fr-ytd-item"><span>Indirect Expenses</span><strong>{fmtMoney(yearView.ytdTotals.indirectExpenses)}</strong></div>
                            <div className="fr-ytd-item"><span>Net Profit</span><strong>{fmtMoney(yearView.ytdTotals.netProfit)}</strong></div>
                            <div className="fr-ytd-item"><span>YTD Margin</span><strong>{fmtPct(yearView.ytdMargin)}</strong></div>
                        </div>
                    </section>

                    <TrendChart
                        data={yearMonthsChart}
                        title={`Monthly trend · ${yearView.year}`}
                        subtitle="Months without data appear as empty bars."
                    />

                    <section className="fr-card">
                        <div className="fr-card-header">
                            <h3>Monthly breakdown</h3>
                        </div>
                        <div className="fr-table-wrap">
                            <table className="fr-table">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th className="fr-num">Sales</th>
                                        <th className="fr-num">Gross Profit</th>
                                        <th className="fr-num">Gross %</th>
                                        <th className="fr-num">Indirect Exp</th>
                                        <th className="fr-num">Net Profit</th>
                                        <th className="fr-num">Net %</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {yearView.months.map(m => (
                                        <tr key={m.month}>
                                            <td><strong>{monthLabel(m.month)}</strong></td>
                                            {m.hasData ? (
                                                <>
                                                    <td className="fr-num">{fmtMoney(m.sales)}</td>
                                                    <td className="fr-num">{fmtMoney(m.grossProfit)}</td>
                                                    <td className="fr-num">{fmtPct(m.grossMargin)}</td>
                                                    <td className="fr-num">{fmtMoney(m.indirectExpenses)}</td>
                                                    <td className="fr-num">{fmtMoney(m.netProfit)}</td>
                                                    <td className="fr-num">{fmtPct(m.netMargin)}</td>
                                                    <td>
                                                        <span className={`fr-badge fr-badge-${(m.approvalStatus || 'PENDING').toLowerCase()}`}>
                                                            {m.approvalStatus || 'PENDING'}
                                                        </span>
                                                    </td>
                                                </>
                                            ) : (
                                                <td colSpan={7} className="fr-empty-cell">No data uploaded</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            {/* YEAR-VS-YEAR MODE */}
            {mode === 'yearCompare' && yearCompare && !loading && (
                <YearComparisonView data={yearCompare} />
            )}

            {!loading && mode === 'compare' && !comparison && !error && (
                <div className="fr-empty-banner">Pick a business unit and two periods to compare.</div>
            )}
            {!loading && mode === 'yearCompare' && !yearCompare && !error && (
                <div className="fr-empty-banner">Pick a business unit and two years to compare.</div>
            )}
        </div>
    );
};

export default AnalyticsTab;
